import { ref, computed } from 'vue';
import { getSettingValue, setSettingValue } from './local-settings';

export const playerMuted = ref(true);
export const playerVolume = ref(1);

let settingsLoaded = false;

function ensureSettingsLoaded() {
  if (settingsLoaded) return;
  settingsLoaded = true;

  const savedMuted = getSettingValue('player.muted');
  if (savedMuted !== null) playerMuted.value = savedMuted;

  const savedVolume = getSettingValue('player.volume');
  if (typeof savedVolume === 'number') playerVolume.value = savedVolume;
}

export const playerVolumeIcon = computed(() => {
  if (playerMuted.value || playerVolume.value === 0) return 'mdi-volume-off';
  if (playerVolume.value < 0.5) return 'mdi-volume-medium';
  return 'mdi-volume-high';
});

function clampVolume(value) {
  return Math.min(1, Math.max(0, value));
}

export function isPlayerAudible(hasAudioTrack, isHovered) {
  if (!hasAudioTrack) return false;
  if (playerMuted.value) return false;
  return isHovered;
}

export function setPlayerMuted(value) {
  ensureSettingsLoaded();
  playerMuted.value = value;
  setSettingValue('player.muted', value);
}

export function setPlayerVolume(value) {
  ensureSettingsLoaded();
  const clamped = clampVolume(value);
  playerVolume.value = clamped;
  setSettingValue('player.volume', clamped);
  if (clamped === 0) {
    setPlayerMuted(true);
  } else if (playerMuted.value) {
    setPlayerMuted(false);
  }
}

export function togglePlayerMute() {
  setPlayerMuted(!playerMuted.value);
}

export function applyPlayerAudio(el, hasAudioTrack = true, isHovered = false) {
  if (!el) return;
  ensureSettingsLoaded();
  el.volume = playerVolume.value;
  el.muted = !isPlayerAudible(hasAudioTrack, isHovered);
}

export async function tryPlayerAutoplay(el, hasAudioTrack = true, isHovered = false) {
  if (!el) return false;

  el.muted = true;
  try {
    await el.play();
    applyPlayerAudio(el, hasAudioTrack, isHovered);
    return true;
  } catch (err) {
    if (err.name === 'NotAllowedError') {
      return false;
    }
    console.warn('Autoplay failed:', err.message);
    return false;
  }
}

export function applyPlayerAudioToVideoJs(player, hasAudioTrack = true, isHovered = false) {
  if (!player || player.isDisposed?.()) return;
  ensureSettingsLoaded();
  player.volume(playerVolume.value);
  player.muted(!isPlayerAudible(hasAudioTrack, isHovered));
}

export async function tryVideoJsAutoplay(player, hasAudioTrack = true, isHovered = false) {
  if (!player) return false;

  player.muted(true);
  try {
    await player.play();
    applyPlayerAudioToVideoJs(player, hasAudioTrack, isHovered);
    return true;
  } catch (err) {
    if (err.name === 'NotAllowedError') {
      return false;
    }
    console.warn('Autoplay failed:', err.message);
    return false;
  }
}

const AUDIO_CODEC_PATTERN = /mp4a(?:\.\d+\.\d+)?|mp3|ac-3|ec-3|opus|flac|aac/i;

function codecsStringHasAudio(codecs) {
  return Boolean(codecs && AUDIO_CODEC_PATTERN.test(codecs));
}

function tracksObjectHasAudio(tracks) {
  return Boolean(tracks?.audio);
}

export function detectVideoElementHasAudio(video) {
  if (!video) return false;

  if (video.audioTracks && video.audioTracks.length > 0) {
    return true;
  }

  if (typeof video.mozHasAudio === 'boolean') {
    return video.mozHasAudio;
  }

  if (typeof video.webkitAudioDecodedByteCount === 'number') {
    return video.webkitAudioDecodedByteCount > 0;
  }

  return false;
}

export function detectHlsHasAudio(hls, data) {
  if (!hls && !data) return false;

  if (data?.audio === true || data?.altAudio === true) {
    return true;
  }

  if (data?.audio || data?.tracks?.audio || tracksObjectHasAudio(data?.tracks)) {
    return true;
  }

  const audioTracks = data?.audioTracks || hls?.allAudioTracks || hls?.audioTracks || [];
  if (audioTracks.length > 0) {
    return true;
  }

  const levels = data?.levels || hls?.levels || [];
  if (levels.some((level) => Boolean(level.audioCodec))) {
    return true;
  }

  for (const level of levels) {
    const codecs = level?.attrs?.CODECS || level?.codecs;
    if (codecsStringHasAudio(codecs)) {
      return true;
    }
  }

  return false;
}

export function detectVideoJsHasAudio(player) {
  if (!player || player.isDisposed?.()) return false;

  try {
    const tracks = player.audioTracks?.();
    if (tracks && tracks.length > 0) return true;
  } catch {
    // ignore
  }

  const vhs = player.tech(true)?.vhs;
  if (vhs?.playlists) {
    const main = vhs.playlists.master;
    const media = typeof vhs.playlists.media === 'function'
      ? vhs.playlists.media()
      : vhs.playlists.media;

    if (codecsStringHasAudio(media?.attributes?.CODECS)) {
      return true;
    }

    if (main?.mediaGroups?.AUDIO) {
      return true;
    }

    if (main?.playlists?.some((playlist) => {
      return playlist.attributes?.AUDIO || codecsStringHasAudio(playlist.attributes?.CODECS);
    })) {
      return true;
    }
  }

  const video = player.el()?.querySelector('video');
  return detectVideoElementHasAudio(video);
}
